import React, { useEffect, useState } from 'react';
import { api } from './api';
import { RangePlayer } from './BeforeAfter';
import { useTakeStamps } from './takeRef';

// An edit of a card that sits on a turn — a word from another take, a level,
// pauses, a cut: a take built on a base. Shown in the turn beside its
// fragment, not only in the card's chat: Before — the take it was built on,
// After — the result, on the stretch the edit touched; Accept / Reject while
// it waits, Undo once accepted (while its take is still the active one).

const fmtTime = (s) => (s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

// The text the edit's Before plays: the remarks' places marked, and a sign
// where each shortened pause sat.
function EditText({ e, turnIndex }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    let live = true;
    api.editText(e.taskId, e.targetId, turnIndex).then((r) => { if (live) setP(r); }).catch(() => {});
    return () => { live = false; };
  }, [e.taskId, e.targetId, e.versionId, turnIndex]);
  if (!p || p.none || !p.text) return null;
  const body = p.text;
  // An edit of pauses shows only its joins: lighting the whole remarked stretch would hide them.
  const marks = p.pauses && p.pauses.length ? [] : (p.marks || []);
  const cuts = [...marks.map((m) => ({ at: m.start, kind: 'open', m })), ...marks.map((m) => ({ at: m.end, kind: 'close', m })), ...(p.pauses || []).map((x) => ({ at: x.at, kind: 'pause', x }))]
    .sort((a, b) => a.at - b.at || (a.kind === 'close' ? -1 : 1));
  const out = [];
  let at = 0;
  let inMark = 0;
  const push = (txt, k) => { if (txt) out.push(inMark ? <mark key={k} title="the place of a remark">{txt}</mark> : <span key={k}>{txt}</span>); };
  cuts.forEach((c, k) => {
    push(body.slice(at, c.at), `t${k}`);
    at = c.at;
    if (c.kind === 'open') inMark += 1;
    else if (c.kind === 'close') inMark = Math.max(0, inMark - 1);
    else out.push(<span key={`p${k}`} className="tk-pause" title={`a pause here was shortened by ${c.x.sec} s`}>‖ −{c.x.sec} s</span>);
  });
  push(body.slice(at), 'tail');
  return (
    <div className="tk-turntext">
      <div className="tk-block-label">Text as played{p.pauses && p.pauses.length ? <span className="tk-muted"> · ‖ marks where a pause was shortened</span> : null}</div>
      <div className="tk-voiced-text tk-fragment-text">{p.clippedLead ? '… ' : ''}{out}{p.clippedTail ? ' …' : ''}</div>
    </div>
  );
}

function EditBlock({ e, card, turnIndex, onOpenCard, onChanged, studio = null }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const stamp = useTakeStamps([e.versionId, e.baseVersionId, e.replacesVersionId]);
  // busy until the parent has reloaded: a second click while the reload is in flight sent a second Accept (22.10.2026)
  const act = async (fn) => { setBusy(true); setError(null); try { await fn(); if (onChanged) await onChanged(); } catch (x) { setError(x.message); } finally { setBusy(false); } };
  // built on a take named by number, not the one active when it was made
  const named = !!(e.replacesVersionId && e.replacesVersionId !== e.baseVersionId);
  const accept = () => act(async () => {
    const r = await api.accept(e.taskId, e.runId, [e.targetId]);
    if (r.conflicts && r.conflicts.length) throw new Error(r.conflicts.map((c) => c.reason).join('; '));
  });
  const reject = () => {
    const note = window.prompt('What did not suit you in this version? You can leave this blank.');
    if (note === null) return;
    act(() => api.reject(e.taskId, e.runId, note, false));
  };
  const undo = () => {
    if (!window.confirm(`Undo this accept? Recording ${stamp(e.replacesVersionId || e.baseVersionId)} will play again. Version ${stamp(e.versionId)} stays in the history.`)) return;
    act(() => api.undo(e.taskId, e.runId, e.targetId));
  };
  const what = e.spliced ? (e.spliced.kind === 'turn' ? 'the turn spliced' : `the ${e.spliced.kind} «${e.spliced.text}» spliced${e.spliced.donor ? ` from point ${e.spliced.donor.pointNo}` : ''}`) : 'an edit of the take';
  // Pauses that were shortened: where they were in Before, where the joins
  // are in After (earlier by what was cut before them).
  const cuts = e.cuts || [];
  const fmtS = (x) => `${Math.floor(x / 60)}:${(x % 60).toFixed(1).padStart(4, '0')}`;
  const cutBefore = (t) => cuts.filter((c) => c.end <= t).reduce((n, c) => n + (c.end - c.start), 0);
  const pauseMarksBefore = cuts.map((c, k) => ({ key: `b${k}`, t: c.start, title: `pause ${fmtS(c.start)}–${fmtS(c.end)} (${(c.end - c.start).toFixed(1)} s cut)` }));
  const pauseMarksAfter = cuts.map((c, k) => ({ key: `a${k}`, t: c.start - cutBefore(c.start), title: `the join where ${(c.end - c.start).toFixed(1)} s of pause was cut` }));
  return (
    <div className={`tk-fragment tk-edit ${e.accepted ? 'accepted' : ''}`}>
      <div className="tk-fragment-head">
        <span className={`tk-chip ${e.accepted ? 'st-done' : 'st-ready'}`}>{e.accepted ? 'accepted' : 'edit to judge'}</span>
        <button type="button" className="tk-linkbtn" onClick={() => onOpenCard && onOpenCard(e.taskId)}>{e.title}</button>
        <span className="tk-muted">{what} · {stamp(e.versionId)}{e.accepted ? ` · accepted by ${e.accepted.by}, ${fmtTime(e.accepted.at)}` : ` · built on ${stamp(e.baseVersionId)}${named ? `, replaces the active take ${stamp(e.replacesVersionId)}${e.replacesIsActive ? '' : ' (no longer active: Accept will refuse)'}` : (e.baseIsActive ? '' : ' (no longer the active take: Accept will refuse)')}`}</span>
      </div>
      {card && card.summary ? (
        <div className="tk-remark"><div className="tk-remark-text open">{card.summary}</div>{card.note ? <div className="tk-muted">Note: {card.note}</div> : null}</div>
      ) : null}
      {e.note ? <div className="tk-muted">{e.note}</div> : null}
      {e.before && e.before.versionId ? <RangePlayer studio={studio} versionId={e.before.versionId} start={e.before.start} end={e.before.end} label={e.accepted ? 'Before · replaced by this accept' : `Before · the take it was built on${e.baseIsActive ? ' · active now' : ''}${named ? ` · named, not the active #${e.replacesVersionId}` : ''}`} marks={cuts.length ? pauseMarksBefore : null} /> : null}
      {e.after && e.after.versionId ? <RangePlayer studio={studio} versionId={e.after.versionId} start={e.after.start} end={e.after.end}  label={`After · the edited take${e.isActive ? ' · active now' : ''}`} marks={cuts.length ? pauseMarksAfter : null} /> : null}
      {cuts.length ? (
        <div className="tk-cuts">
          <span className="tk-muted">{cuts.length} pause{cuts.length === 1 ? '' : 's'} shortened{e.removedSec ? `, −${e.removedSec} s in all` : ''}</span>
        </div>
      ) : null}
      <EditText e={e} turnIndex={turnIndex} />
      {error ? <div className="tk-error">{error}</div> : null}
      <div className="tk-accept">
        {e.accepted ? (
          <>
            <button type="button" className="tk-btn tk-btn-ghost" onClick={undo} disabled={busy || !e.isActive}>Undo accept</button>
            <span className="tk-muted">Undo accept restores recording {stamp(e.replacesVersionId || e.baseVersionId)}. This version can be compared and accepted again.</span>
          </>
        ) : (
          <>
            <button type="button" className="tk-btn tk-btn-primary" onClick={accept} disabled={busy}>Accept</button>
            <button type="button" className="tk-btn tk-btn-ghost" onClick={reject} disabled={busy}>Reject</button>
            <span className="tk-muted">Accept replaces the current recording {stamp(e.replacesVersionId || e.baseVersionId)} with version {stamp(e.versionId)}. If the current recording has already changed, the edit will not be applied. Publishing to the live quest is a separate step.</span>
          </>
        )}
      </div>
    </div>
  );
}

export default function TurnEdits({ turn, locationUuid = null, field = null, onOpenCard, onChanged }) {
  if (!turn || !turn.edits || !turn.edits.length) return null;
  return <>{turn.edits.map((e) => <EditBlock key={`${e.taskId}/${e.runId}/${e.targetId}`} e={e} studio={locationUuid ? { locationUuid, field } : null} card={(turn.cards || []).find((c) => c.taskId === e.taskId)} turnIndex={turn.i} onOpenCard={onOpenCard} onChanged={onChanged} />)}</>;
}
